import React from 'react';
import { motion } from 'framer-motion';
import { 
  Briefcase, 
  Target, 
  Users, 
  Brain, 
  Shield, 
  ArrowRight, 
  CheckCircle,
  Settings,
  Network,
  Cloud,
  Lock,
  FileCheck,
  RefreshCw
} from 'lucide-react';

interface ConsultingOverviewProps {
  setCurrentPage: (page: string) => void;
}

const ConsultingOverview: React.FC<ConsultingOverviewProps> = ({ setCurrentPage }) => {
  const services = [
    {
      icon: Settings,
      title: "Managed IT Services",
      description: "Comprehensive IT management and support services for your organization.",
      features: [
        "24/7 Infrastructure monitoring",
        "System administration",
        "Network management",
        "Technical support"
      ],
      link: "managed-services"
    },
    {
      icon: Briefcase,
      title: "Professional IT Services",
      description: "Expert consulting and implementation services for your technology needs.",
      features: [
        "Strategic IT planning",
        "Technology assessment",
        "Solution implementation",
        "Project management"
      ],
      link: "professional-services"
    },
    {
      icon: Network,
      title: "Global Delivery Model",
      description: "Worldwide service delivery through our global network of experts.",
      features: [
        "24/7 global coverage",
        "Local expertise",
        "Standardized processes",
        "Consistent quality"
      ],
      link: "global-delivery"
    }
  ];

  const expertise = [
    {
      icon: Shield,
      title: "Security Strategy",
      description: "Develop comprehensive security strategies aligned with your business objectives.",
      items: [
        "Risk assessment and management",
        "Security roadmap development",
        "Policy and procedure creation",
        "Compliance framework design"
      ]
    },
    {
      icon: Cloud,
      title: "Digital Transformation",
      description: "Guide your organization through successful digital transformation initiatives.",
      items: [
        "Technology modernization",
        "Process optimization",
        "Cloud migration strategy",
        "Digital innovation"
      ]
    },
    {
      icon: Lock,
      title: "Security Architecture",
      description: "Design and implement robust security architectures for your infrastructure.",
      items: [
        "Architecture assessment",
        "Security controls design",
        "Technology selection",
        "Implementation guidance"
      ]
    },
    {
      icon: FileCheck,
      title: "Compliance & Risk",
      description: "Ensure regulatory compliance and effective risk management.",
      items: [
        "Compliance assessment",
        "Risk management",
        "Audit preparation",
        "Policy development"
      ]
    }
  ];

  const benefits = [
    {
      icon: Target,
      title: "Strategic Approach",
      description: "Data-driven strategies aligned with your business objectives"
    },
    {
      icon: Users,
      title: "Expert Team",
      description: "Access to certified professionals with deep industry expertise"
    },
    {
      icon: Brain,
      title: "Innovation Focus",
      description: "Cutting-edge solutions leveraging latest technologies"
    },
    {
      icon: RefreshCw,
      title: "Continuous Support",
      description: "Ongoing guidance and support throughout your journey"
    }
  ];

  const handleServiceClick = (link: string) => {
    setCurrentPage(link);
  };

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-6"
          >
            <Briefcase className="h-16 w-16 text-primary animate-glow" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="text-gradient">Consulting Services</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted max-w-3xl mx-auto"
          >
            Expert guidance and strategic solutions to transform your organization's security and technology landscape
          </motion.p>
        </div>

        {/* Core Services */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Our Consulting Services</h2>
            <p className="text-lg text-muted">Comprehensive consulting solutions for your organization</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 flex flex-col hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                    <service.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold ml-4">{service.title}</h3>
                </div>
                
                <p className="text-muted mb-6">{service.description}</p>
                
                <ul className="space-y-3 mb-6 flex-grow">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                      <span className="text-muted">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button 
                  onClick={() => handleServiceClick(service.link)}
                  className="w-full btn-primary mt-auto"
                >
                  <span className="flex items-center justify-center">
                    Learn More
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </span>
                </button>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Areas of Expertise */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Areas of Expertise</h2>
            <p className="text-lg text-muted">Specialized consulting services across key domains</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {expertise.map((area, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                    <area.icon className="h-7 w-7 text-white" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-2xl font-semibold">{area.title}</h3>
                    <p className="text-muted mt-1">{area.description}</p>
                  </div>
                </div>
                
                <ul className="space-y-3">
                  {area.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                      <span className="text-muted">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Benefits */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Why Choose Our Consulting Services</h2>
            <p className="text-lg text-muted">Partner with experts who understand your business needs</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="glass-panel rounded-xl p-6 hover-card"
              >
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg mb-6">
                  <benefit.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                <p className="text-muted">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <div className="glass-panel rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-6">Ready to Transform Your Business?</h2>
            <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
              Schedule a consultation with our experts to discuss your organization's needs and discover how we can help
            </p>
            <button 
              onClick={() => handleServiceClick('get-started')}
              className="btn-primary"
            >
              <span className="flex items-center justify-center">
                Request Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </span>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ConsultingOverview;